import type { FC } from "react";
import { useRef, useState } from "react";

import noop from "lodash/noop";
import styled from "styled-components";

import { ChipDownContainer } from "@similarweb/ui-components";

import { i18nFilter } from "app/filters/i18nFilter";
import { DifficultyFilter } from "app/pages/digital-marketing/difficulty-filter/DifficultyFilter";
import {
    getKeywordDifficultyFilterText,
    getSelectedRangeLimits,
} from "app/pages/website-analysis/traffic-sources/search/components/filters/keywordDifficultyFilter.utils";

const DifficultyFilterWrapper = styled.div`
    height: 164px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: auto;
`;

const KEYWORD_DIFFICULTY_FILTER_CHIP_DOWN_PROPS = {
    disabled: false,
    closeOnItemClick: false,
    width: 430,
};

type KeywordDifficultyFilterProps = {
    rangeLimits: [number, number];
    difficultyFromValue: number;
    difficultyToValue: number;
    setNewDifficultyRange: (value: [number, number]) => void;
    onRemoveFilterCallback?: VoidFunction;
    onOpen?: VoidFunction;
    onApply?: (selectedRangeValue: [number, number]) => void;
    filterTextKey?: string;
};

type websiteKeywordsPageFiltersConstantsTypes = {
    RAGE_LIMITS: [number, number];
};

export const WEBSITE_KEYWORDS_PAGE_FILTERS_CONSTANTS: websiteKeywordsPageFiltersConstantsTypes = {
    RAGE_LIMITS: [1, 100],
};

const safeDifficultyValue: (value?: string | number) => number = (value) => {
    if (typeof value === "string") {
        return Number(value);
    }
    return value;
};

export const KeywordDifficultyFilter: FC<KeywordDifficultyFilterProps> = (props) => {
    const {
        rangeLimits,
        setNewDifficultyRange,
        difficultyFromValue,
        difficultyToValue,
        onRemoveFilterCallback,
        onApply = noop,
        onOpen = noop,
        filterTextKey = "keywords.table.filters.difficulty.button",
    } = props;
    const keywordDifficultyChipDown = useRef(undefined);
    safeDifficultyValue(difficultyFromValue);
    safeDifficultyValue(difficultyToValue);
    const [selectedRangeValue, setSelectedRangeValue] = useState<[number, number]>(
        getSelectedRangeLimits(difficultyFromValue, difficultyToValue, rangeLimits),
    );

    const getDifficultyForSetter = (values: [number, number]): [number, number] => {
        const areValuesEqualToLimits = values[0] === rangeLimits[0] && values[1] === rangeLimits[1];
        return areValuesEqualToLimits ? [null, null] : values;
    };

    const applyNewRangeAndClosePopUp = () => {
        setNewDifficultyRange(getDifficultyForSetter(selectedRangeValue));
        keywordDifficultyChipDown?.current?.closePopup();
        onApply(selectedRangeValue);
    };

    const onRemoveFilter = () => {
        // Reset range state
        setNewDifficultyRange(getDifficultyForSetter(rangeLimits));
        setSelectedRangeValue(rangeLimits);
        if (onRemoveFilterCallback) {
            onRemoveFilterCallback();
        }
    };

    const shouldPresentResetButton = !(JSON.stringify(rangeLimits) === JSON.stringify(selectedRangeValue));

    const onToggle = (isOpen: boolean, isOutsideClick: boolean) => {
        if (isOutsideClick) {
            applyNewRangeAndClosePopUp();
        } else if (isOpen) {
            onOpen();
        }
    };

    return (
        <ChipDownContainer
            {...KEYWORD_DIFFICULTY_FILTER_CHIP_DOWN_PROPS}
            ref={keywordDifficultyChipDown}
            selectedText={getKeywordDifficultyFilterText(
                difficultyFromValue,
                difficultyToValue,
                rangeLimits[0],
                rangeLimits[1],
            )}
            buttonText={i18nFilter()(filterTextKey)}
            onCloseItem={onRemoveFilter}
            onToggle={onToggle}
            noResultsMessage={i18nFilter()("dropdown.no.results")}
        >
            {[
                <DifficultyFilterWrapper key={"DifficultyFilterWrapper"}>
                    <DifficultyFilter
                        rangeLimits={rangeLimits}
                        selectedRangeValue={selectedRangeValue}
                        applyNewRange={applyNewRangeAndClosePopUp}
                        shouldPresentResetButton={shouldPresentResetButton}
                        setFilterRangeValue={setSelectedRangeValue}
                    />
                </DifficultyFilterWrapper>,
            ]}
        </ChipDownContainer>
    );
};
